// src/components/GameStoreCard.js
// ============================================================================
// GAME STORE CARD COMPONENT
//
// Displays a game card with context-aware actions:
// - In desktop client: Download → Installing → Play flow
// - On web: "Get Desktop App" prompt for downloadable games
//
// States:
// - not_owned: Show price/free + "Get" button
// - owned_not_installed: Show "Download" button
// - downloading: Show progress bar + "Cancel" button
// - installing: Show "Installing..." spinner
// - installed: Show "Play" button
// - needs_update: Show "Update" button
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Image,
} from 'react-native';
import { isDesktopApp, downloadGame, cancelDownload, installGame, launchGame, getInstalledGames } from '../utils/platform';

// Game state constants
const GAME_STATE = {
  NOT_OWNED: 'not_owned',
  OWNED_NOT_INSTALLED: 'owned_not_installed',
  DOWNLOADING: 'downloading',
  INSTALLING: 'installing',
  INSTALLED: 'installed',
  NEEDS_UPDATE: 'needs_update',
  RUNNING: 'running',
};

export default function GameStoreCard({
  game,
  libraryEntry,
  onAddToLibrary,
  onShowDesktopPrompt,
  compact = false,
}) {
  const [gameState, setGameState] = useState(GAME_STATE.NOT_OWNED);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [localInstallInfo, setLocalInstallInfo] = useState(null);

  const inDesktopClient = isDesktopApp();

  // Determine game state based on library entry and local install status
  useEffect(() => {
    determineGameState();
  }, [libraryEntry, localInstallInfo]);

  // Check local install status when in desktop client
  useEffect(() => {
    if (inDesktopClient) {
      checkLocalInstall();
    }
  }, [inDesktopClient]);

  const checkLocalInstall = async () => {
    try {
      const installed = await getInstalledGames();
      if (installed[game.gameId]) {
        setLocalInstallInfo(installed[game.gameId]);
      }
    } catch (err) {
      console.error('[GameCard] Failed to check local install:', err);
    }
  };

  const determineGameState = () => {
    // Not in library = not owned
    if (!libraryEntry) {
      setGameState(GAME_STATE.NOT_OWNED);
      return;
    }

    // Check if locally installed (desktop client)
    if (localInstallInfo?.installed) {
      // Check if update needed
      if (libraryEntry.needsUpdate) {
        setGameState(GAME_STATE.NEEDS_UPDATE);
      } else {
        setGameState(GAME_STATE.INSTALLED);
      }
      return;
    }

    // Check cloud install status
    if (libraryEntry.installed) {
      setGameState(GAME_STATE.INSTALLED);
    } else {
      setGameState(GAME_STATE.OWNED_NOT_INSTALLED);
    }
  };

  // Handle "Get" / Add to Library
  const handleAddToLibrary = async () => {
    setError(null);
    try {
      await onAddToLibrary?.(game.gameId);
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle Download
  const handleDownload = async () => {
    if (!inDesktopClient) {
      onShowDesktopPrompt?.();
      return;
    }

    setError(null);
    setGameState(GAME_STATE.DOWNLOADING);
    setDownloadProgress(0);

    // Get download URL for current platform
    const platform = (await window.critcashDesktop?.getPlatformInfo?.())?.platform || 'win32';
    const downloadUrl = game.downloadUrls?.[platform];

    if (!downloadUrl) {
      setError(`No download available for ${platform}`);
      setGameState(GAME_STATE.OWNED_NOT_INSTALLED);
      return;
    }

    const result = await downloadGame(game.gameId, downloadUrl, (progress) => {
      setDownloadProgress(progress);
    });

    if (result.success) {
      // Auto-install after download
      handleInstall();
    } else {
      setError(result.error || 'Download failed');
      setGameState(GAME_STATE.OWNED_NOT_INSTALLED);
    }
  };

  // Handle Cancel Download
  const handleCancelDownload = async () => {
    await cancelDownload(game.gameId);
    setGameState(GAME_STATE.OWNED_NOT_INSTALLED);
    setDownloadProgress(0);
  };

  // Handle Install
  const handleInstall = async () => {
    setGameState(GAME_STATE.INSTALLING);
    setError(null);

    const result = await installGame(game.gameId);

    if (result.success) {
      setGameState(GAME_STATE.INSTALLED);
      // Refresh local install info
      checkLocalInstall();
    } else {
      setError(result.error || 'Installation failed');
      setGameState(GAME_STATE.OWNED_NOT_INSTALLED);
    }
  };

  // Handle Play
  const handlePlay = async () => {
    setError(null);
    const result = await launchGame(game.gameId);
    
    if (!result.success) {
      setError(result.error || 'Failed to launch game');
    }
  };

  // Handle Update (same as download for now)
  const handleUpdate = () => {
    handleDownload();
  };

  // Render the action button based on state
  const renderActionButton = () => {
    switch (gameState) {
      case GAME_STATE.NOT_OWNED:
        return (
          <TouchableOpacity 
            style={[styles.actionButton, styles.getButton]}
            onPress={handleAddToLibrary}
          >
            <Text style={styles.actionButtonText}>
              {game.priceCents ? `$${(game.priceCents / 100).toFixed(2)}` : 'Get Free'}
            </Text>
          </TouchableOpacity>
        );

      case GAME_STATE.OWNED_NOT_INSTALLED:
        return (
          <TouchableOpacity 
            style={[styles.actionButton, styles.downloadButton]}
            onPress={handleDownload}
          >
            <Text style={styles.actionButtonText}>
              {inDesktopClient ? '⬇️ Download' : '💻 Get Desktop App'}
            </Text>
          </TouchableOpacity>
        );

      case GAME_STATE.DOWNLOADING:
        return (
          <View style={styles.downloadingContainer}>
            <View style={styles.progressBar}>
              <View style={[styles.progressFill, { width: `${downloadProgress}%` }]} />
            </View>
            <View style={styles.progressRow}>
              <Text style={styles.progressText}>{Math.round(downloadProgress)}%</Text>
              <TouchableOpacity onPress={handleCancelDownload}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        );

      case GAME_STATE.INSTALLING:
        return (
          <View style={[styles.actionButton, styles.installingButton]}>
            <ActivityIndicator size="small" color="#000" />
            <Text style={[styles.actionButtonText, { marginLeft: 8 }]}>Installing...</Text>
          </View>
        );

      case GAME_STATE.INSTALLED:
        return (
          <TouchableOpacity 
            style={[styles.actionButton, styles.playButton]}
            onPress={handlePlay}
          >
            <Text style={styles.actionButtonText}>▶️ Play</Text>
          </TouchableOpacity>
        );

      case GAME_STATE.NEEDS_UPDATE:
        return (
          <TouchableOpacity 
            style={[styles.actionButton, styles.updateButton]}
            onPress={handleUpdate}
          >
            <Text style={styles.actionButtonText}>🔄 Update</Text>
          </TouchableOpacity>
        );

      case GAME_STATE.RUNNING:
        return (
          <View style={[styles.actionButton, styles.runningButton]}>
            <Text style={styles.runningText}>Running</Text>
          </View>
        );

      default:
        return null;
    }
  };

  if (compact) {
    return (
      <View style={styles.compactCard}>
        <View style={styles.compactInfo}>
          <Text style={styles.compactName} numberOfLines={1}>{game.name}</Text>
          {game.category === 'first_party' && (
            <View style={styles.officialBadge}>
              <Text style={styles.officialBadgeText}>CritCash</Text>
            </View>
          )}
        </View>
        {renderActionButton()}
        {error && <Text style={styles.errorText}>{error}</Text>}
      </View>
    );
  }

  return (
    <View style={styles.card}>
      {/* Cover Image */}
      <View style={styles.coverContainer}>
        {game.coverImage ? (
          <Image source={{ uri: game.coverImage }} style={styles.coverImage} />
        ) : (
          <View style={styles.placeholderCover}>
            <Text style={styles.placeholderText}>{game.name?.[0] || '?'}</Text>
          </View>
        )}
        {game.category === 'first_party' && (
          <View style={styles.firstPartyRibbon}>
            <Text style={styles.ribbonText}>CritCash Original</Text>
          </View>
        )}
      </View>

      {/* Game Info */}
      <View style={styles.infoContainer}>
        <Text style={styles.gameName} numberOfLines={2}>{game.name}</Text>
        <Text style={styles.developer}>{game.developer}</Text>
        
        {/* Genres */}
        {game.genres?.length > 0 && (
          <View style={styles.genreRow}>
            {game.genres.slice(0, 2).map((genre, i) => (
              <View key={i} style={styles.genreTag}>
                <Text style={styles.genreText}>{genre}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Platforms */}
        {game.platforms?.length > 0 && (
          <Text style={styles.platforms}>
            {game.platforms.join(' • ')}
          </Text>
        )}
      </View>

      {/* Action Button */}
      <View style={styles.actionContainer}>
        {renderActionButton()}
        {error && <Text style={styles.errorText}>{error}</Text>}
      </View>
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#111',
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#222',
    width: 220,
    marginRight: 16,
    marginBottom: 16,
  },
  coverContainer: {
    width: '100%',
    height: 120,
    backgroundColor: '#1a1a1a',
    position: 'relative',
  },
  coverImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  placeholderCover: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
  },
  placeholderText: {
    fontSize: 48,
    color: '#333',
    fontWeight: 'bold',
  },
  firstPartyRibbon: {
    position: 'absolute',
    top: 8,
    left: -24,
    backgroundColor: '#FCD000',
    paddingVertical: 2,
    paddingHorizontal: 28,
    transform: [{ rotate: '-45deg' }],
  },
  ribbonText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#000',
  },
  infoContainer: {
    padding: 12,
  },
  gameName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 4,
  },
  developer: {
    fontSize: 12,
    color: '#888',
    marginBottom: 8,
  },
  genreRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  genreTag: {
    backgroundColor: '#222',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    marginRight: 6,
    marginBottom: 4,
  },
  genreText: {
    fontSize: 10,
    color: '#aaa',
  },
  platforms: {
    fontSize: 10,
    color: '#666',
  },
  actionContainer: {
    padding: 12,
    paddingTop: 0,
  },
  actionButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  getButton: {
    backgroundColor: '#FCD000',
  },
  downloadButton: {
    backgroundColor: '#2563eb',
  },
  installingButton: {
    backgroundColor: '#555',
  },
  playButton: {
    backgroundColor: '#22c55e',
  },
  updateButton: {
    backgroundColor: '#f59e0b',
  },
  runningButton: {
    backgroundColor: '#1a3a1a',
    borderWidth: 1,
    borderColor: '#22c55e',
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000',
  },
  runningText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#22c55e',
  },
  downloadingContainer: {
    width: '100%',
  },
  progressBar: {
    height: 8,
    backgroundColor: '#222',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#2563eb',
    borderRadius: 4,
  },
  progressRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressText: {
    fontSize: 12,
    color: '#888',
  },
  cancelText: {
    fontSize: 12,
    color: '#e57373',
  },
  errorText: {
    fontSize: 11,
    color: '#e57373',
    marginTop: 8,
    textAlign: 'center',
  },
  officialBadge: {
    backgroundColor: '#FCD000',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 8,
  },
  officialBadgeText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#000',
  },

  // Compact variant styles
  compactCard: {
    backgroundColor: '#111',
    borderRadius: 8,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#222',
    marginBottom: 8,
  },
  compactInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  compactName: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '500',
  },
});